/**
 * useProductFilters Hook - Story 6.2: Create My Products Page
 * Manages filter state for product listing
 */

import React from 'react';
import type { ProductFilters, ProductStatus, ProductCategory, UseProductFiltersReturn } from '../types/products.types';

/**
 * Default filter state
 */
const DEFAULT_FILTERS: ProductFilters = {
  status: 'ACTIVE',  // Default to ACTIVE products only - products < 20 kg are automatically deactivated
  search: '',
  sortBy: 'createdDate',
  sortOrder: 'desc',
  page: 1,
  pageSize: 20,
};

/**
 * useProductFilters Hook
 * Manages filter state for products listing with localStorage persistence
 *
 * @returns Filter state and setter functions
 *
 * @example
 * const {
 *   filters,
 *   setFilters,
 *   updateStatus,
 *   updateSearch,
 *   goToPage
 * } = useProductFilters();
 *
 * // Update individual filters
 * updateStatus('ACTIVE');
 * updateSearch('biogas');
 * goToPage(2);
 *
 * // Or update multiple at once
 * setFilters({ status: 'INACTIVE', search: 'seeds' });
 */
export function useProductFilters(): UseProductFiltersReturn {
  // Initialize from localStorage synchronously to avoid flash of wrong filter
  const [filters, setFiltersState] = React.useState<ProductFilters>(() => {
    try {
      const savedFilters = localStorage.getItem('marketplace_product_filters');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        return { ...DEFAULT_FILTERS, ...parsed, page: 1 };
      }
    } catch (error) {
      console.error('[useProductFilters] Error loading filters from localStorage:', error);
    }
    return DEFAULT_FILTERS;
  });

  // Save filters to localStorage whenever they change
  React.useEffect(() => {
    try {
      localStorage.setItem('marketplace_product_filters', JSON.stringify(filters));
    } catch (error) {
      console.error('[useProductFilters] Error saving filters to localStorage:', error);
    }
  }, [filters]);

  /**
   * Update filters (partial update)
   */
  const setFilters = React.useCallback((newFilters: Partial<ProductFilters>) => {
    setFiltersState((current) => ({
      ...current,
      ...newFilters,
      // Always reset to page 1 when filters change (except when explicitly setting page)
      page: newFilters.page !== undefined ? newFilters.page : 1,
    }));
  }, []);

  /**
   * Reset to default filters
   */
  const resetFilters = React.useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  /**
   * Update status filter only
   */
  const updateStatus = React.useCallback((status: 'ALL' | ProductStatus) => {
    setFilters({ status, page: 1 });
  }, [setFilters]);

  /**
   * Update search filter only
   */
  const updateSearch = React.useCallback((search: string) => {
    setFilters({ search, page: 1 });
  }, [setFilters]);

  /**
   * Update sorting
   */
  const updateSort = React.useCallback(
    (sortBy: 'createdDate' | 'inquiryCount' | 'name', sortOrder: 'asc' | 'desc') => {
      setFilters({ sortBy, sortOrder });
    },
    [setFilters]
  );

  /**
   * Go to specific page
   */
  const goToPage = React.useCallback((page: number) => {
    setFiltersState((current) => ({
      ...current,
      page: Math.max(1, page),
    }));
  }, []);

  return {
    filters,
    setFilters,
    resetFilters,
    updateStatus,
    updateSearch,
    updateSort,
    goToPage,
  };
}

/**
 * Export types
 */
export type { UseProductFiltersReturn, ProductFilters };
